import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Loader2, MapPin, Store } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { getLastPosition } from "@/components/GeoProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImagePick = ({ label, url, onPick, busy, testId, round }) => (
  <label className={`relative overflow-hidden border border-dashed border-white/20 cursor-pointer flex items-center justify-center bg-[#0F172A] ${round ? "w-20 h-20 rounded-full" : "h-24 flex-1 rounded-2xl"}`} data-testid={`${testId}-label`}>
    {busy ? <Loader2 className="animate-spin" /> : url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-slate-400 flex flex-col items-center gap-1"><ImagePlus size={16} />{label}</span>}
    <input type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} data-testid={`${testId}-input`} />
  </label>
);

// Profil restaurant : logo, couverture, cuisine, ouverture manuelle, position sur la carte
export const RestaurantProfileEditor = ({ user }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const [address, setAddress] = useState(user?.address || "");

  useEffect(() => {
    axios.get(`${API}/restaurant/settings`).then((r) => setSt(r.data)).catch(() => setSt({ cuisines: [] }));
  }, []);

  const save = async (patch, msg) => {
    try {
      const { data } = await axios.put(`${API}/restaurant/settings/profile`, patch);
      setSt(data);
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const upload = async (file, field) => {
    if (!file) return;
    setBusy(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("vendor_id", user.id);
      const r = await axios.post(`${API}/products/upload-image`, fd);
      await save({ [field]: `${BACKEND_URL}${r.data.image_url}` }, field === "logo_url" ? "Logo mis à jour" : "Couverture mise à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setBusy("");
    }
  };

  const locate = () => {
    const p = getLastPosition();
    if (!p) return toast.error("Position GPS indisponible : autorisez la localisation");
    save({ latitude: p.lat, longitude: p.lng, address: address || undefined }, "Restaurant placé sur la carte WÔMA");
  };

  if (!st) return null;
  return (
    <section className="bg-[#1E293B] border border-white/5 rounded-2xl p-4 space-y-4" data-testid="restaurant-profile-editor">
      <h3 className="text-xs font-black uppercase tracking-widest flex items-center gap-2"><Store size={14} className="text-[#D4AF37]" /> Profil du restaurant</h3>
      <div className="flex gap-3 items-center">
        <ImagePick label="Logo" url={st.logo_url} busy={busy === "logo_url"} onPick={(f) => upload(f, "logo_url")} testId="resto-logo" round />
        <ImagePick label="Photo de couverture" url={st.cover_url} busy={busy === "cover_url"} onPick={(f) => upload(f, "cover_url")} testId="resto-cover" />
      </div>
      <select value={st.cuisine || ""} onChange={(e) => save({ cuisine: e.target.value }, "Catégorie enregistrée")} className="w-full h-10 rounded-md bg-[#0F172A] border border-white/10 px-3 text-sm" data-testid="resto-cuisine-select">
        <option value="" disabled>Catégorie de cuisine</option>
        {(st.cuisines || []).map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <div className="flex items-center justify-between rounded-xl bg-[#0F172A] px-3 py-2.5">
        <div>
          <p className="text-sm font-bold">{st.manual_closed ? "Fermé temporairement" : "Ouvert selon vos horaires"}</p>
          <p className="text-[10px] text-slate-400">Fermez d'un geste en cas d'imprévu (rupture, pause…)</p>
        </div>
        <Switch className="shrink-0" checked={!st.manual_closed} onCheckedChange={(v) => save({ manual_closed: !v }, v ? "Restaurant rouvert" : "Restaurant fermé temporairement")} data-testid="resto-manual-open-switch" />
      </div>
      <div className="flex gap-2">
        <Input placeholder="Adresse du restaurant" value={address} onChange={(e) => setAddress(e.target.value)} className="bg-[#0F172A] border-white/10" data-testid="resto-address-input" />
        <button type="button" onClick={locate} className="shrink-0 rounded-full bg-[#D4AF37] text-[#0F172A] px-3 text-[10px] font-black uppercase flex items-center gap-1" data-testid="resto-locate-btn"><MapPin size={12} /> Ma position</button>
      </div>
    </section>
  );
};
